import { Component, ErrorInfo, ReactNode } from 'react';
import { ErrorFallbackPage } from './ErrorFallbackPage';
import { FirebaseCrashlytics } from '@capacitor-community/firebase-crashlytics';
import * as Sentry from '@sentry/react';
import Expr from '@yumi/legacy/lib/Expr';
import { getErrorDiagnostic } from './errorDiagnostics';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  eventId?: string;
  occurredAt?: string;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error, occurredAt: new Date().toISOString() };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ hasError: true, error });
    try {
      const eventId = Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: info.componentStack,
          },
        },
      });
      this.setState({ eventId });
      void Sentry.flush(2000).catch(() => undefined);
    } catch {
      // Observability must not replace the original fallback with another error.
    }
    Expr.whenInNativePhone(async () => {
      await FirebaseCrashlytics.recordException({
        message: getErrorDiagnostic(error).message,
      }).catch(() => undefined);
    });
  }

  override render() {
    if (this.state.hasError) {
      return <ErrorFallbackPage error={this.state.error} eventId={this.state.eventId} occurredAt={this.state.occurredAt} />;
    }
    return this.props.children;
  }
}
